// ─────────────────────────────────────────────────────────────────────────
// Stat definitions registry (Ken 2026-08-10).
//
// A single ORDERED list of the plain-language definitions for every stat that
// needs an explanation. Order here is the canonical display order — keep new
// entries in the position the stat appears on the page. We haven't decided yet
// whether these surface as tooltips, an on-page glossary, or both; keeping the
// text in one ordered place means that decision is a rendering change, not a
// content hunt.
//
// `key` matches the tile/stat it explains. `label` is the human name. `def` is
// the one-line explanation.
// ─────────────────────────────────────────────────────────────────────────

export type StatDefinition = {
  key: string
  label: string
  def: string
}

export const STAT_DEFINITIONS: StatDefinition[] = [
  {
    key: 'deduped_email',
    label: 'Filed Emails',
    def: 'Substantive emails sent and received per working day (Mon–Fri). Excludes newsletters/digests, personal mail, and automated notifications (DocuSign, Google Docs comments, Drive shares). Counts a message cc’d to both Heather and Maria once.',
  },
  {
    key: 'email_issues',
    label: 'Email Issues',
    def: 'Unique issues raised in professional email sent or received, per working day (Mon–Fri). Excludes personal mail, digests (e.g., “NACUA”), and automated notifications (DocuSign, Google Docs comments, Drive shares).',
  },
  {
    key: 'documents',
    label: 'Documents',
    def: 'Documents sent or received on professional email, per working day (Mon–Fri). Google Docs, Word, PDF, Excel, and similar files (not inline images, and not newsletters or automated notifications). Deduped across Heather and Maria; counted each time a document is transmitted.',
  },
  {
    key: 'new_matters',
    label: '(Estimated) New Matters',
    def: 'New matters opened per working day (Mon–Fri), dated by the arrival of each matter’s founding email; excludes matters since archived. ESTIMATE: the raw count runs high because the matcher over-creates matters (fragments we later merge or ignore), so it’s scaled down by a temporary fragmentation factor (currently ×0.78) until automatic de-fragmentation lands.',
  },
  {
    key: 'total_matters',
    label: 'Total',
    def: 'All active matters in the library right now (excludes archived/closed).',
  },
  {
    key: 'refile_rate',
    label: 'Refile rate',
    def: 'The AI’s own correction signal: re-files divided by filings. Only measurable since re-file logging shipped.',
  },
  {
    key: 'fragmentation_rate',
    label: 'Fragmentation rate',
    def: 'A narrower error: the founding email of a matter was later moved out — the decision to open the matter was itself wrong. Fragmentations divided by matters created.',
  },
]

const BY_KEY: Record<string, StatDefinition> = Object.fromEntries(
  STAT_DEFINITIONS.map((d) => [d.key, d]),
)

/** Look up a stat definition by key (undefined if not registered). */
export function statDef(key: string): StatDefinition | undefined {
  return BY_KEY[key]
}

/** Convenience: just the explanation text (empty string if none). */
export function statDefText(key: string): string {
  return BY_KEY[key]?.def ?? ''
}
